<template>
  <a-dropdown
    v-model:open="isOpen"
    :placement="placement"
    :trigger="['click']"
    :getPopupContainer="getPopupContainer"
    @openChange="onOpenChange"
    :builtinPlacements="{
      bottomLeft: { points: ['tl', 'bl'], overflow: { adjustY: true } },
      topLeft: { points: ['bl', 'tl'], overflow: { adjustY: true } },
    }"
    @click.stop
  >
    <a-tooltip placement="top" color="var(--tooltip-bg)">
      <template #title>
        <span class="text_color">Add Members to {{ appName }}</span>
      </template>
      <div
        class="flex items-center justify-center p-1 rounded-lg cursor-pointer hover:bg-[--white-opacity-8] three_dot_icon opacity-0 group-hover:opacity-100 transition-opacity"
        :class="{ 'opacity-100': isOpen }"
        @click.stop
      >
        <ThemeImage
          class="size-4"
          :icon="imageNames.MORE_VERTICAL"
          alt="more_options"
        />
      </div>
    </a-tooltip>

    <template #overlay>
      <div v-if="isAppStoreV2" @click.stop>
        <div v-if="showAddMembers" @click.stop>
          <AddMembersDropdown
            :appId="appId"
            :appName="appName"
            @dropdown-open="isInnerOpen = true"
            @dropdown-close="isInnerOpen = false"
            @close="closeAddMembers"
            @members-updated="noop"
          />
        </div>
        <a-menu v-else>
          <a-menu-item key="add-members" @click="openAddMembers">
            <div class="flex items-center gap-2">
              <ThemeImage
                class="size-4"
                :icon="imageNames.ADD_MEMBER_OPTION"
                alt="add_members"
              />
              <span class="text-sm">Add Members</span>
            </div>
          </a-menu-item>
        </a-menu>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { isAppStoreV2 as isV2 } from "@/src/utils/appStoreUtils.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import AddMembersDropdown from "@/src/components/AddMembersDropdown.vue";

const props = defineProps({
  appId: { type: Number, required: true },
  appName: { type: String, required: true },
  placement: { type: String, default: "bottomLeft" },
  alwaysVisible: { type: Boolean, default: false },
});

const emit = defineEmits(["open-change", "mode-change"]);

const store = useStore();
const isOpen = ref(false);
const isInnerOpen = ref(false);
const showAddMembers = ref(false);

const isAppStoreV2 = computed(() => isV2(store));

const getPopupContainer = (triggerNode) => {
  if (typeof document === 'undefined') {
    return triggerNode?.parentNode || triggerNode;
  }
  
  const appContainers = document.querySelectorAll('#app_v2');
  if (appContainers.length >= 2) {
    const secondAppContainer = appContainers[1];
    const flexContainer = secondAppContainer.querySelector('.flex.flex-row.flex-1.overflow-hidden');
    if (flexContainer) {
      const sidebarSection = flexContainer.querySelector('#sidebar_section');
      if (sidebarSection) {
        return sidebarSection;
      }
      return flexContainer;
    }
  }
  
  return triggerNode?.parentNode || triggerNode;
};

const onOpenChange = (open) => {
  if (!open && isInnerOpen.value) {
    isOpen.value = true;
    return;
  }

  isOpen.value = open;
  if (!open) {
    showAddMembers.value = false;
    emit("mode-change", "menu");
  }
  emit("open-change", open);
};

const openAddMembers = () => {
  showAddMembers.value = true;
  emit("mode-change", "add-members");
};

const closeAddMembers = () => {
  showAddMembers.value = false;
  isInnerOpen.value = false;
  emit("mode-change", "menu");

  isOpen.value = true;
};

const noop = () => {};
</script>
